import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { IMonthValue } from '../../util/DateUtils';

const DECIMALS = 3;
const ROUNDED = 0;

export interface ISummaryReportTableRow {
  name: string;
  codes: string[];
  description: string;
  kg: number;
  eur: number;
  eurKg: number;
  handling: number;
  transport: number;
  receiving: number;
  containers: number;
  fee: number;
  pomKg: number;
}

export interface ISummaryReportTableMetrics {
  handling: number;
  transport: number;
  receiving: number;
  containers: number;
}

export interface ISummaryReportTableTotals {
  handling: number;
  transport: number;
  receiving: number;
  containers: number;
  kg: number;
  eur: number;
  eurKg: number;
  fee: number;
  pomKg: number;
}

export interface ISummaryReportTableLamps {
  name: string;
  codes: string;
  description: string;
  fee_fi: number;
  fee_al: number;
  pomPcs_fi: number;
  pomPcs_al: number
}

export interface ISummaryReportTableProps {
  reportDate: IMonthValue;
  data: ISummaryReportTableRow[];
  totals: ISummaryReportTableTotals;
  metrics: ISummaryReportTableMetrics;
  lamps: ISummaryReportTableLamps;
  onSave: () => void;
  onReset: () => void;
}

const COLUMNS = [
  'name',
  'codes',
  'description',
  'kg',
  'handling',
  'transport',
  'receiving',
  'containers',
  'eur',
  'eurKg',
  'fee',
  'pomKg',
];

const SORTABLE_COLUMNS = [
  'name',
  'codes',
  'description',
  'kg',
  'handling',
  'transport',
  'receiving',
  'containers',
  'eur',
  'eurKg',
  'fee',
  'pomKg',
];

const COLUMNS_LAMPS = [
  'name',
  'codes',
  'description',
  'fee_fi',
  'fee_al',
  'pomPcs_fi',
  'pomPcs_al'
];

const SummaryReportTable: React.FunctionComponent<ISummaryReportTableProps> = (props: ISummaryReportTableProps) => {
  const [dataTable] = useState(props.data);
  const [orderColumn, setOrderColumn] = useState('');
  const [orderDirection, setOrderDirection] = useState(1); // 1 | -1

  const setOrdering = (column: string) => {
    if (orderColumn !== column) {
      setOrderColumn(column);
      setOrderDirection(1);
    } else {
      setOrderDirection(-orderDirection);
    }
  }

  const getTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS.forEach((name) => {
      const classes = ['header'];
      let icon = null;
      let sortable = false;
      if (SORTABLE_COLUMNS.includes(name)) {
        classes.push('sortable');
        sortable = true;
      }
      if (name === orderColumn) {
        classes.push('sortby');
        icon = orderDirection === 1 ?
          <FontAwesomeIcon className="icon" icon="sort-up" color="white" /> : <FontAwesomeIcon className="icon" icon="sort-down" color="white" />
      } else if (sortable) {
        icon = <FontAwesomeIcon className="icon" icon="sort" color="lightgray" />
      }
      headers.push(
        <th key={name}>
          <span className={classes.join(' ')} onClick={() => sortable && setOrdering(name)}>
          <FormattedMessage id={`summaryreport.datatable.${name}`}/> {icon}
        </span></th>
      );
    })
    return headers;
  }

  const getLampsTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS_LAMPS.forEach((name) => {
      headers.push(
        <th key={name} className="header">
          <FormattedMessage id={`summaryreport.datatable.${name}`}/>
        </th>
      );
    })
    return headers;
  }

  const comparer = (a: ISummaryReportTableRow, b: ISummaryReportTableRow) => {
    let value = 0;
    if (orderColumn !== '') {
      switch(orderColumn) {
        case 'name':
          value = a.name.localeCompare(b.name);
          break;
        case 'description':
          value = a.description.localeCompare(b.description);
          break;
        case 'kg':
          value = a.kg - b.kg;
          break;
        case 'handling':
          value = a.handling - b.handling;
          break;
        case 'transport':
          value = a.transport - b.transport;
          break;
        case 'receiving':
          value = a.receiving - b.receiving;
          break;
        case 'containers':
          value = a.containers - b.containers;
          break;
        case 'eur':
          value = a.eur - b.eur;
          break;
        case 'eurKg':
          value = a.eurKg - b.eurKg;
          break;
        case 'fee':
          value = a.fee - b.fee;
          break;
        case 'pomKg':
          value = a.pomKg - b.pomKg;
          break;
      }
    }
    return orderDirection * value;
  }

  const getDataTable = () => {
    const options: { }[] = [];
    if (dataTable && Array.isArray(dataTable)) {
      dataTable.sort(comparer).forEach((row : ISummaryReportTableRow, index) => {
        options.push(
          <tr key={index}>
            <td>{row.name}</td>
            <td>{row.codes.join(', ')}</td>
            <td><FormattedMessage id={`summaryreport.browse.datatable.${row.description.toLowerCase()}`}/></td>
            <td className="right"><FormattedNumber value={row.kg} maximumFractionDigits={ROUNDED} minimumFractionDigits={ROUNDED}/></td>
            <td className="right"><FormattedNumber value={row.handling} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.transport} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.receiving} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.containers} maximumFractionDigits={ROUNDED} minimumFractionDigits={ROUNDED}/></td>
            <td className="right"><FormattedNumber value={row.eur} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.eurKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.fee} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
            <td className="right"><FormattedNumber value={row.pomKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
          </tr>
        );
      });
    }
    return options;
  }

  const getTotals = () => {
    return (
      <tr className="totals">
        <td><FormattedMessage id={'summaryreport.browse.datatable.totals'}/></td>
        <td></td>
        <td></td>
        <td className="right"><FormattedNumber value={props.totals.kg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.handling} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.transport} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.receiving} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.containers} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.eur} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.eurKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.fee} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
        <td className="right"><FormattedNumber value={props.totals.pomKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
      </tr>
    )
  }

  return (
    <div className="SummaryReportTable generalReportImportResult">
      <div className="result-header d-flex flex-row">
        <div className="reportResultHeader">
          <h3 className="monthRange">
            <FormattedDate  value={new Date(props.reportDate.year, props.reportDate.month)}
                            month="long" year="numeric" />
          </h3>
          <h3 className="reportHeader">
            <FormattedMessage id="summaryreport.costreport"/>
          </h3>
        </div>
      </div>
      <div className="table-responsive">
        <Table className="table table-sm table-striped">
          <thead className="reportDataHeader table table-bordered">
            <tr>
              <td colSpan={4} className="header"><FormattedMessage id="summaryreport.metrics"/></td>
              <td><span className="metrics"><FormattedNumber value={props.metrics.handling} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></span></td>
              <td><span className="metrics"><FormattedNumber value={props.metrics.transport} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></span></td>
              <td><span className="metrics"><FormattedNumber value={props.metrics.receiving} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></span></td>
              <td><span className="metrics"><FormattedNumber value={props.metrics.containers} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></span></td>
              <td><FormattedMessage id="summaryreport.datatable.eurKg"/></td>
              <td></td>
            </tr>
            <tr className="table-headers">
              { getTableHeaders() }
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable() }
            { getTotals() }
          </tbody>
        </Table>
      </div>
      <div className="table-responsive">
        <Table className="table table-sm table-striped">
          <thead className="reportDataHeader table table-bordered">
            <tr className="table-headers">
              { getLampsTableHeaders() }
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            <tr>
              <td>{props.lamps.name}</td>
              <td>{props.lamps.codes}</td>
              <td><FormattedMessage id={`summaryreport.browse.datatable.${props.lamps.description.toLowerCase()}`}/></td>
              <td className="right"><FormattedNumber value={props.lamps.fee_fi} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
              <td className="right"><FormattedNumber value={props.lamps.fee_al} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
              <td className="right"><FormattedNumber value={props.lamps.pomPcs_fi} maximumFractionDigits={ROUNDED} minimumFractionDigits={ROUNDED}/></td>
              <td className="right"><FormattedNumber value={props.lamps.pomPcs_al} maximumFractionDigits={ROUNDED} minimumFractionDigits={ROUNDED}/></td>
            </tr>
          </tbody>
        </Table>
      </div>
      <div className="action-buttons">
        <button className="btn btn-secondary" type="reset" onClick={() => props.onReset()}>
          <FormattedMessage id="summaryreport.reset"/>
        </button>
        <button className="btn btn-primary" onClick={() => props.onSave()}>
          <FormattedMessage id="summaryreport.save"/>
        </button>
      </div>
    </div>
  )
}

export default SummaryReportTable;
